import { useMutation } from '@tanstack/react-query'
import { TxRaw } from 'cosmjs-types/cosmos/tx/v1beta1/tx'
import { assert } from '@/utils'
import { CHAIN_CONFIG, CHAIN_INFO_LIST } from '@/config'
import { calculateFeeWithHelper, deductFeeFromTxAmountWhileEmptyStrd } from '@/wallet-utils'
import { generateIbcTimeoutTimestampBigInt } from '@/wallet-utils/ibc'
import { MutationParameters } from './types'
import { useSelectedDenom } from '@/contexts/denom'
import { useSelectedChain, useStrideChain, useStrideChainBalance } from '@/contexts/wallet'
import { ibc } from 'stridejs'

export interface SignIbcTransferMutationParameters {
  amount: number
}

const useSignWithdrawMutation = ({ setWithdrawRaw }: MutationParameters) => {
  const [denom] = useSelectedDenom()

  const { address: strideAddress, getSigningStargateClient } = useStrideChain()

  const { address: selectedAddress } = useSelectedChain()

  const { data: strideBalances } = useStrideChainBalance()

  const handleMutation = async (amount: string): Promise<TxRaw> => {
    assert(strideAddress, 'You are unable to send token without connecting your Stride wallet.')

    assert(selectedAddress, `You are unable to send token without connecting your ${denom} wallet.`)

    assert(strideBalances, `Unable to send st tokens out of Stride without your Stride wallet balances.`)

    const createMsgTransfer = (amount: string) => {
      return ibc.applications.transfer.v1.MessageComposer.withTypeUrl.transfer({
        sourcePort: 'transfer',
        sourceChannel: CHAIN_CONFIG[denom].withdrawChannel,
        token: {
          amount,
          denom: CHAIN_CONFIG[denom].ibcDenom
        },
        sender: strideAddress,
        receiver: selectedAddress,
        memo: '',
        timeoutHeight: {
          revisionHeight: 0n,
          revisionNumber: 0n
        },
        timeoutTimestamp: generateIbcTimeoutTimestampBigInt()
      })
    }

    const msgTransferForSimulation = createMsgTransfer(amount)
    const client = await getSigningStargateClient()
    const gas = await client.simulate(strideAddress, [msgTransferForSimulation], '')
    const { fee, deductible } = calculateFeeWithHelper(CHAIN_INFO_LIST[denom].stakeCurrency.coinMinimalDenom, gas)

    // Allot a small portion of the user's tokens for the transaction gas fee
    //
    // Testing Guide (Preparation):
    // 1. Setup a fresh account, and send a small amount of tokens to it (e.g., 1 SAGA).
    // 2. IBC transfer the tokens to the wallet's Stride address.
    // 3. Proceed with "Withdraw" flow.
    // 4. Inspect the transaction amount and you'll find that it should be less than the full amount.
    //
    // @TODO: Only deduct if the user does not have enough tokens for gas
    const amountWithSafetyDeduction = deductFeeFromTxAmountWhileEmptyStrd(
      strideBalances.balances,
      BigInt(amount),
      deductible
    )

    const msgTransfer = createMsgTransfer(amountWithSafetyDeduction.toString())
    return await client.sign(strideAddress, [msgTransfer], fee, '')
  }

  const handleSuccess = (raw: TxRaw) => {
    setWithdrawRaw(raw)
  }

  return useMutation({
    mutationFn: handleMutation,
    onSuccess: handleSuccess
  })
}

export { useSignWithdrawMutation }
